/**
 * Normalizes UUID identifiers to standard format
 * 
 * Converts UUIDs to lowercase, removes whitespace, and ensures proper dash formatting.
 * Handles both dashed (8-4-4-4-12) and undashed (32 character) UUID formats.
 * 
 * @param id - The UUID string to normalize
 * @returns Normalized UUID in lowercase with standard dash format (8-4-4-4-12)
 * 
 * @example
 * ```ts
 * // Already formatted UUID
 * normalizeId('550e8400-e29b-41d4-a716-446655440000')
 * // Returns: '550e8400-e29b-41d4-a716-446655440000'
 * 
 * // Undashed UUID
 * normalizeId('550e8400e29b41d4a716446655440000')
 * // Returns: '550e8400-e29b-41d4-a716-446655440000'
 * 
 * // UUID with whitespace
 * normalizeId('550E8400-E29B-41D4-A716-446655440000 ')
 * // Returns: '550e8400-e29b-41d4-a716-446655440000'
 * ```
 */
export function normalizeId(id: string): string {
  const compact = id.replace(/\s+/g, '').toLowerCase();

  const dashedUuid =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  if (dashedUuid.test(compact)) {
    return compact;
  }

  const undashedUuid = /^[0-9a-f]{32}$/i;
  if (undashedUuid.test(compact)) {
    return [
      compact.slice(0, 8),
      compact.slice(8, 12),
      compact.slice(12, 16),
      compact.slice(16, 20),
      compact.slice(20, 32),
    ].join('-');
  }

  return compact;
}
